import { QueryResponseCamelCase } from 'types'
import { AppointmentScoringRecord } from './types'

type GetAppointmentScoringsResponse = QueryResponseCamelCase<AppointmentScoringRecord>

/**
 * `transformResponse` for the appointment-scorings endpoint.
 *
 * ⚠️ Client-side defense-in-depth — NOT a true PII boundary. The wire `categories` object
 * carries `summary`, `customerName`, `providerName` (all dropped here) and a free-text
 * `explanation` per category. This strip rebuilds `categories` to value-only and drops the
 * other PII before the payload reaches app code, the react-query cache, or any log/error —
 * real wins. The per-category `explanation` is intentionally **retained** as a parallel
 * `explanations` map for the category-detail popover (shown for any scored transcript category
 * with an explanation, not only deviations); it can echo names from the transcript
 * and must never be logged or sent to analytics. The strings have already crossed the wire
 * and are briefly materialized by `response.json()` (and are visible in the Network tab). The
 * authoritative fix for omitting the explanation to score-only consumers is server-side:
 * ta.summary-service should omit them for those consumers (follow-up).
 */
const stripScoringPii = async (response: Response): Promise<GetAppointmentScoringsResponse> => {
  const body = await response.json()
  const records: unknown[] = Array.isArray(body?.data) ? body.data : []

  const data = records.flatMap((entryRecord) => {
    // Untrusted wire data — drop a non-object element (e.g. a null) entirely. Downstream
    // consumers (e.g. `mapScoringsToTranscriptScores`) destructure every element, so passing a
    // `null` through would throw; this keeps the boundary's contract "an array of records".
    if (!entryRecord || typeof entryRecord !== 'object') {
      return []
    }

    const record = entryRecord as Record<string, unknown>
    const rawCategories = record.categories
    let categories: Record<string, number> | null = null
    let explanations: Record<string, string> | null = null

    // Arrays pass `typeof === 'object'` but aren't a category map — treat as none.
    if (rawCategories && typeof rawCategories === 'object' && !Array.isArray(rawCategories)) {
      // `Object.create(null)` (not `{}`) so a malicious `__proto__`/`constructor` wire key
      // can't pollute the prototype chain of the accumulated maps.
      categories = Object.create(null)
      explanations = Object.create(null)

      for (const [key, entry] of Object.entries(rawCategories as Record<string, unknown>)) {
        // Keep only the numeric per-category score. `summary`/`customerName`/`providerName`
        // are strings (skipped here).
        if (entry && typeof entry === 'object') {
          const value = (entry as { value?: unknown }).value

          if (typeof value === 'number') {
            ;(categories as Record<string, number>)[key] = value

            // Retained for the category-detail tooltip (any scored category with an
            // explanation, not only deviations). Scoped to entries that also carry a numeric
            // score — exactly the set the checklist can display — so we never cache free-text
            // PII for value-less entries that are never shown. Empty strings are treated as none.
            const explanation = (entry as { explanation?: unknown }).explanation

            if (typeof explanation === 'string' && explanation) {
              ;(explanations as Record<string, string>)[key] = explanation
            }
          }
        }
      }
    }

    // Allowlist: return ONLY the fields modeled by `AppointmentScoringRecord`. Spreading
    // `...record` would pass through every top-level wire field, so a future new field (more
    // PII, a model ARN, etc.) would silently leak into app code and the react-query cache.
    return [
      {
        appointmentGid: record.appointmentGid as AppointmentScoringRecord['appointmentGid'],
        categories,
        createdAt: record.createdAt as AppointmentScoringRecord['createdAt'],
        explanations,
        maxTotalScore: record.maxTotalScore as AppointmentScoringRecord['maxTotalScore'],
        status: record.status as AppointmentScoringRecord['status'],
        totalScore: record.totalScore as AppointmentScoringRecord['totalScore'],
      },
    ]
  })

  return { ...body, data } as GetAppointmentScoringsResponse
}

export { stripScoringPii }
export type { GetAppointmentScoringsResponse }
